// @flow

import React, { useState } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';

import { WebsiteDefaultPage as BaseWebsiteDefaultPage } from 'components/Page';
import { PageType } from 'Constants';
import { OriginationFees } from 'data/global/footer/legal/numbered';
import { useImages } from 'hooks';
import { absolute, Color, flex, Layout, maxWidthContainer, topBottomMargin, Width } from 'styles';
import { Button, ImageLoader } from 'components/Gui';
import { LogoMarkets } from 'components/Svg';
import { Toggle } from 'components/Content';

const METADATA = {
  title: 'Financial products to simplify finances, faster',
  description: `At Markets Direct, we’re transforming home equity by creating a fast, simple and transparent process to bring you closer to your financial goals. We'll show you how to use home equity to consolidate debt, pay for home improvement or fund your retirement. It's quick and easy. Let’s unlock the potential of your largest asset.`,
};

const PAGE_TYPE = {
  product: PageType.PRODUCT.NONE,
};

const WebsiteDefaultPage = styled(BaseWebsiteDefaultPage)`
  width: 100% !important;
`;

const Wrapper = styled.div`
  width: 100%;
  ${flex()};
  flex-direction: column;
  ${topBottomMargin([Layout.NAV_HEIGHT_MOBILE, 0], [Layout.NAV_HEIGHT_DESKTOP, 0], [Layout.NAV_HEIGHT_DESKTOP, 0])};
`;

const Hero = styled.div`
  position: relative;
  width: 100%;
  height: 500px;
  padding: 4px 0;
  box-sizing: border-box;
  background: ${Color.MARKETS_RED};
  ${flex()};
  flex-direction: column;

  > div.bg {
    ${absolute('4px', 0, '4px', 0)};
    z-index: 1;
  }

  > div.logo {
    position: relative;
    z-index: 2;
    background: black;
    padding: 10px;
    ${flex()};
    margin-bottom: 40px;

    svg {
      width: 100px;

      path,
      rect,
      polygon {
        fill: white;
      }
    }
  }

  > div.text {
    position: relative;
    z-index: 2;
    margin-top: 30px;

    p {
      color: white;
      text-align: center;
      font-weight: 700;
      font-size: 2.4rem;
      padding: 0 20px;

      @media (min-width: ${Width.BREAK_MD}px) {
        font-size: 3rem;
      }
    }
  }

  > div.xs {
    position: relative;
    z-index: 2;
    height: 100px;
    background: rgba(232, 41, 43, 0.4);
    width: 100%;
    ${flex()};

    @media (min-width: ${Width.BREAK_MD}px) {
      height: 140px;
    }

    > div {
      width: 300px;
      transform: translateY(18%);

      @media (min-width: ${Width.BREAK_MD}px) {
        width: 500px;
        transform: translateY(18%);
      }
    }
  }
`;

const Text = styled.div`
  width: 100%;
  background: white;

  p {
    font-size: 2rem;
    font-weight: 500;
  }
  ${flex()};
  > div {
    padding: 40px 0;
    ${maxWidthContainer(880)};
  }
`;

const Products = styled.div`
  width: 100%;
  background: white;

  @media (min-width: ${Width.BREAK_MD}px) {
    padding-bottom: 60px;
  }

  > div {
    padding: 40px 0;
    ${maxWidthContainer(1080)};
    ${flex('center', 'flex-end')};
    flex-direction: column;

    @media (min-width: ${Width.BREAK_MD}px) {
      flex-direction: row;
    }

    h2 {
      font-size: 4rem;
      color: ${Color.MARKETS_RED};
      margin-bottom: 30px;
    }

    div.image {
      width: 200px;
      height: 200px;
      flex: 0 0 auto;
      overflow: hidden;
      position: relative;

      @media (min-width: ${Width.BREAK_MD}px) {
        margin: 0 30px;
      }

      @media (min-width: ${Width.BREAK_XL}px) {
        margin: 0 150px;
      }
    }
  }
`;

const TextTwo = styled.div`
  width: 100%;
  background: ${Color.MARKETS_RED};
  position: relative;

  > div.bg {
    ${absolute()};
  }

  p {
    color: white;
    font-size: 2rem;
    text-align: center;
    margin-bottom: 20px;
  }
  ${flex()};
  > div.text {
    position: relative;
    z-index: 1;
    padding: 40px 0;
    ${maxWidthContainer(Width.XL)};
    ${flex()};
    flex-direction: column;
  }
`;

const Data = [
  'Optimize your portfolio margin, leverage your stocks to trade futures and FX',
  'Trade in over 500 US listed equities',
  'Ultra-convenient trading from your mobile device',
  'Build your stock portfolio with fractional and notional shares',
  'Trade and hedge your leveraged FX with futures, utilizing one wallet and cross margining technology',
];

const Items = styled.div`
  ${flex('space-between', 'center')};
  flex-wrap: wrap;
  padding: 30px 0 0 0;

  @media (min-width: ${Width.BREAK_MD}px) {
    padding: 30px 100px 0 100px;
  }

  > div {
    width: 100%;
    flex: 0 0 auto;
    ${flex('flex-start', 'center')};
    margin-bottom: 10px;

    @media (min-width: ${Width.BREAK_MD}px) {
      width: 100%;
      margin-bottom: 16px;
    }

    &:nth-of-type(even) {
      p {
        color: ${Color.MARKETS_RED};
      }
    }

    > div {
      width: 48px;
      height: 48px;
      margin-right: 12px;
      position: relative;
      flex: 0 0 auto;
    }
  }
`;

const ToggleData = [
  {
    image: 'forex',
    title: 'Forex',
    text: (
      <>
        <p>
          The Forex market is far and away the largest traded market in the world, with an estimated daily turnover of $7 trillion, vs.
          84 billion in equities worldwide.
        </p>
        <Button url="/products">Learn More</Button>
      </>
    ),
  },
  {
    image: 'futures',
    title: 'Futures',
    text: (
      <>
        <p>
          Built upon the acclaimed CQG Integrated Client desktop with its extensive market data feeds, advanced technical analysis,
          charting, and order management tools.
        </p>
        <Button url="/products">Learn More</Button>
      </>
    ),
  },
  {
    image: 'equities',
    title: 'US Equities',
    text: (
      <>
        <p>
          Markets Direct has partnered with DriveWealth, LLC to offer our clients a simple, low cost solution to invest in US equities.
        </p>
        <Button url="/products">Learn More</Button>
      </>
    ),
  },
  {
    image: 'etfs',
    title: 'ETFs',
    text: (
      <>
        <p>
          Exchange Traded Funds (ETF’s) are investment funds that trade on exchanges much like stocks. Invest in a basket of securities
          that are highly liquid and transparent.
        </p>
        <Button url="/products">Learn More</Button>
      </>
    ),
  },
];

const PageContainer = ({ data }: { data: any }) => {
  const getImage = useImages(data);
  const [index, setIndex] = useState(0);
  return (
    <WebsiteDefaultPage
      page={PAGE_TYPE}
      metaData={{
        ...METADATA,
        image: getImage('markets-bg'),
      }}
      footerLegal={{
        numbered: OriginationFees,
      }}>
      <Wrapper>
        <Hero>
          <div className="bg">
            <ImageLoader fitContainer image={getImage('bg')} />
          </div>
          <div className="logo">{LogoMarkets}</div>
          <div className="xs">
            <div>
              <ImageLoader image={getImage('access-logo')} />
            </div>
          </div>
          <div className="text">
            <p>{`Multi-Asset Trading Platform
              Trade the Global Markets from One Account`}</p>
          </div>
        </Hero>
        <Text>
          <div>
            <p>
              Introducing ACCESS, the premier technology platform that brings together the world’s exchange traded and OTC markets - in
              an all-in-one cross margined account. Built in partnership with respected industry leaders CQG and NetDania, trade the
              global markets with features including:
            </p>
            <Items>
              {Data.map((data, index) => (
                <div key={`TheData-${index}`}>
                  <div>
                    <ImageLoader fitContainer image={getImage(index + 1)} />
                  </div>
                  <p>{data}</p>
                </div>
              ))}
            </Items>
          </div>
        </Text>
        <TextTwo>
          <div className="image">
            <ImageLoader fitContainer image={getImage('middle-bg')} />
          </div>
          <div className="text">
            <p>
              {`Get ahead of the competition and changes in regulations and capital flow restrictions.     
Ask us how you can expand your product offering with our Markets Direct white label solution.`}
            </p>
            <Button url={`https://fxdemo.fxdd.com/olreg/applyonline/LiveRegistrationInfoAction?ibrokerCode=xsmt&languageId=en_US`}>
              OPEN ACCOUNT
            </Button>
          </div>
        </TextTwo>
        <Products>
          <div>
            <div>
              <h2>Products</h2>
              <Toggle setIndex={setIndex} content={ToggleData} />
            </div>
            <div className="image">
              <ImageLoader fitContainer image={getImage(ToggleData[index].image)} />
            </div>
          </div>
        </Products>
      </Wrapper>
    </WebsiteDefaultPage>
  );
};

export default PageContainer;

export const query = graphql`
  query {
    imageToWebP: allFile(filter: { relativeDirectory: { eq: "pages/homepage" }, extension: { nin: ["svg", "gif"] } }) {
      edges {
        node {
          ...ImageToWebP
        }
      }
    }
    otherImage: allFile(filter: { relativeDirectory: { eq: "pages/homepage" }, extension: { in: ["svg", "gif"] } }) {
      edges {
        node {
          ...OtherImage
        }
      }
    }
  }
`;
